import {
  ArslActionAddVariablesToColumn,
  ArsListingColumn,
  ArsListingState,
  ArsListingVariableType,
} from "../interface";
import { resolvePlaceholderText } from "./buildApplyDroppedColumnsAction";

/**
 * Appends dropped variables to an existing listing column and updates its patterns.
 *
 * @param state - Current ARS listing state
 * @param action - Add-variables action for the target column
 * @returns Updated ARS listing state, or the original state when the column is missing
 */
export const arsl_addVariablesToColumn = <T>(
  state: ArsListingState<T>,
  action: ArslActionAddVariablesToColumn,
): ArsListingState<T> => {
  const targetColumn = state.bodyData.arsListingColumns.find((col) => col.id === action.colId);

  if (!targetColumn) {
    return state;
  }

  const textToT = state.settings.editorProps.textToT;
  const getTextFromT = state.settings.editorProps.getTextFromT;
  const newDisplayPattern = [
    ...targetColumn.displayPattern.map((displayPattern) => getTextFromT(displayPattern)),
    ...action.variables.map((variable) => resolvePlaceholderText(variable.dataType)),
  ].join("/");
  const newHeaderText = [
    getTextFromT(targetColumn.headerText),
    ...action.variables.map((variable) =>
      variable.description?.trim() !== "" ? variable.description : variable.name,
    ),
  ].join("/");
  const newResultPattern = [
    targetColumn.resultPattern,
    ...action.variables.map((variable) => `{${variable.name.toLowerCase()}}`),
  ].join("/");
  const newColumn: ArsListingColumn<T> = {
    ...targetColumn,
    displayPattern: [textToT(newDisplayPattern)],
    resultPattern: newResultPattern,
    headerText: textToT(newHeaderText),
    variables: [
      ...targetColumn.variables,
      ...action.variables.map((variable) => ({
        id: crypto.randomUUID(),
        dataset: variable.dataset,
        variable: variable.name,
        variableType: "displayOnly" as ArsListingVariableType,
      })),
    ],
  };

  const newState = {
    ...state,
    changed: true,
    bodyData: {
      ...state.bodyData,
      arsListingColumns: state.bodyData.arsListingColumns.map((col) =>
        col.id === action.colId ? newColumn : col,
      ),
    },
  };

  // Check new datasets
  if (newColumn.variables.some((v) => !state.datasets.has(v.dataset))) {
    newState.datasets = new Set([...state.datasets, ...newColumn.variables.map((v) => v.dataset)]);
  }

  return newState;
};
